import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import type { ColumnDef } from '@tanstack/react-table';

import { Can } from '@/components/can/Can';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import type { useDynamicLocale } from '@/hoslog-intl';
import { Translation } from '@/hoslog-intl';
import { LockCloseIcon, LockOpenIcon, TrashIcon } from '@/icons';
import { commaSeparator } from '@/lib/commaSeparator';
import { formatDate } from '@/lib/dateFormatter';
import { Affiliate, AFFILIATE_INFO_STATUS } from '@/services/affiliate/affiliate.types';
import { Link } from '@tanstack/react-router';
import { Edit, KeyRoundIcon } from 'lucide-react';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';
type OpenConfirmationProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
  onCancel: () => void;
}) => void;

interface GetColumnsProps {
  openConfirmation: OpenConfirmationProps;
  mutateAsyncResetPassword: ({ affiliateId }: { affiliateId: string }) => Promise<unknown>;
  mutateAsyncAffilateStatus: ({ affiliateId, status }: { affiliateId: string; status: string }) => Promise<unknown>;
  t: ReturnType<typeof useDynamicLocale>['t'];
}

export function getColumns({
  openConfirmation,
  mutateAsyncResetPassword,
  mutateAsyncAffilateStatus,
  t,
}: GetColumnsProps): ColumnDef<Affiliate>[] {
  return [
    {
      accessorKey: 'id',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('userManagement.affiliates.table.columnTitle.sn')} />
      ),
      cell: ({ row, table }) => (
        <div className="text-xs font-medium truncate max-w-[10rem]">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'affiliateInfo',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('userManagement.affiliates.table.columnTitle.affiliateInfo')} />
      ),
      cell: ({ row }) => (
        <div className="text-xs font-medium truncate max-w-[20rem]">{row.getValue('affiliateInfo')}</div>
      ),
      meta: {
        label: 'Affiliate Info',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'emailAddress',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('userManagement.affiliates.table.columnTitle.emailAddress')} />
      ),
      cell: ({ row }) => {
        const email: string = row.getValue('emailAddress');
        return (
          <a href={`mailto:${email}`} className="text-xs text-link font-medium underline">
            {email}
          </a>
        );
      },

      meta: {
        label: 'Email Address',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'mobileNumber',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('userManagement.affiliates.table.columnTitle.phoneNumber')} />
      ),
      cell: ({ row }) => <div className="text-xs font-medium">{row.getValue('mobileNumber')}</div>,

      enableHiding: false,
    },
    {
      accessorKey: 'rewardPoints',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('userManagement.affiliates.table.columnTitle.rewardBalance')} />
      ),
      cell: ({ row }) => {
        const value = Number(row.getValue('rewardPoints'));
        return <div className="text-right text-xs font-medium">{commaSeparator(value.toFixed(0))}円</div>;
      },
      meta: {
        label: 'Reward Balance',
        align: 'right',
      },
      enableHiding: false,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('userManagement.affiliates.table.columnTitle.status')} />
      ),
      cell: ({ cell }) => {
        const status = cell.getValue<Affiliate['status']>();
        return (
          <div className="text-xs font-medium">
            {status === AFFILIATE_INFO_STATUS.ACTIVE ? (
              <div className="text-success">
                <Translation id={'common.page.table.active'} />
              </div>
            ) : (
              <div className="text-destructive-text">
                <Translation id={'common.page.table.blocked'} />
              </div>
            )}
          </div>
        );
      },
      enableHiding: false,
      enableColumnFilter: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('userManagement.affiliates.table.columnTitle.createdDate')} />
      ),
      cell: ({ row }) => {
        return (
          <div className="text-xs font-medium whitespace-nowrap">
            {formatDate(Number(row.getValue('createdDate')), 'YYYY/MM/DD')}
          </div>
        );
      },

      enableHiding: false,
      enableColumnFilter: true,
    },
    {
      accessorKey: 'updatedDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('userManagement.affiliates.table.columnTitle.updatedDate')} />
      ),
      cell: ({ row }) => {
        return (
          <div className="text-xs font-medium whitespace-nowrap">
            {formatDate(Number(row.getValue('updatedDate')), 'YYYY/MM/DD')}
          </div>
        );
      },
      enableHiding: false,
    },
    {
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('userManagement.affiliates.table.columnTitle.action')} />
      ),
      meta: {
        label: 'Action',
      },
      id: 'actions',
      cell: ({ row }) => {
        const status = row.original.status.toLowerCase();
        const name = row.original.affiliateInfo;
        return (
          <div className="flex items-center space-x-2">
            <Can rule={{ anyOf: [PERMS.affiliate.detailView] }}>
              <Link
                to="/users/affiliates/$affiliateId"
                params={{ affiliateId: row.original.agentId }}
                search={{ breadcrumbReturnTo: window.location.href }}
                className={status === 'b' ? 'hidden' : undefined}
              >
                <Edit size={16} className="cursor-pointer text-foreground-caption hover:text-foreground-detail" />
              </Link>
            </Can>
            <Can rule={{ anyOf: [PERMS.affiliate.passwordReset] }}>
              <KeyRoundIcon
                size={16}
                className={`cursor-pointer text-warning hover:text-warning ${status === 'b' ? 'hidden' : ''}`}
                onClick={() =>
                  openConfirmation({
                    title: t('common.page.modal.resetPassword.title'),
                    description: t('userManagement.affiliate.resetPasswordModel.message', { affiliateName: name }),
                    cancelLabel: t('common.page.modal.no'),
                    size: 'lg',
                    actionLabel: t('common.page.modal.confirm'),
                    onAction: async () => {
                      await mutateAsyncResetPassword({
                        affiliateId: row.original.agentId,
                      });
                    },
                    onCancel: () => {},
                    variant: 'warning',
                  })
                }
              />
            </Can>
            <Can rule={{ anyOf: [PERMS.affiliate.statusUpdate] }}>
              <Button
                variant={'ghost'}
                className="p-0 "
                onClick={() =>
                  openConfirmation({
                    title: t('common.page.modal.confirmation.title'),
                    description:
                      status === 'a'
                        ? t('userManagement.affiliate.blockUnblockModel.blockMessage', { affiliateName: name })
                        : t('userManagement.affiliate.blockUnblockModel.unblockMessage', { affiliateName: name }),
                    cancelLabel: t('common.page.modal.no'),
                    actionLabel: t('common.page.modal.confirm'),
                    size: 'lg',
                    onAction: async () => {
                      await mutateAsyncAffilateStatus({
                        affiliateId: row.original.agentId,
                        status:
                          row.original.status === AFFILIATE_INFO_STATUS.ACTIVE
                            ? AFFILIATE_INFO_STATUS.BLOCKED
                            : AFFILIATE_INFO_STATUS.ACTIVE,
                      });
                    },
                    onCancel: () => {},
                    variant: 'warning',
                  })
                }
              >
                {row.original.status !== AFFILIATE_INFO_STATUS.ACTIVE ? (
                  <LockOpenIcon size={16} className="cursor-pointer text-status-info-soft " />
                ) : (
                  <LockCloseIcon className="cursor-pointer text-destructive-text  " size={16}></LockCloseIcon>
                )}
              </Button>
            </Can>
            {row.original.status === AFFILIATE_INFO_STATUS.ACTIVE && (
              <Can rule={{ anyOf: [PERMS.affiliate.manage] }}>
                <TrashIcon
                  size={16}
                  className="cursor-pointer text-destructive-text hover:text-destructive-text "
                  onClick={() =>
                    openConfirmation({
                      title: t('common.page.modal.confirmation.title'),
                      description: t('storeManagement.basicStores.confirmationModal.deleteItem'),
                      cancelLabel: t('common.page.modal.no'),
                      actionLabel: t('common.page.modal.confirm'),
                      size: 'lg',
                      onAction: async () => {
                        await mutateAsyncAffilateStatus({
                          affiliateId: row.original.agentId,
                          status: AFFILIATE_INFO_STATUS.DELETE,
                        });
                      },
                      onCancel: () => {},
                      variant: 'destructive',
                    })
                  }
                />
              </Can>
            )}
          </div>
        );
      },
      size: 40,
    },
  ];
}
